import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';
import { SettingsProvider } from '@services/settings/settings';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { ShootProofDataProvider } from '@services/integrations/shootProof/shootProof.data.service';
import { InvoicesDataProvider } from '../../../../../../../shared/services/invoice/invoices-data-service/invoices.data.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import {
	GetRedirectPicTimeUrl,
	ShootProofGalleriesBrand
} from '@models/models';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { IntegrationsServiceProvider } from '@services/integrations/integrations.service';

@Component({
	selector: 'create-new-gallery-modal',
	template: require('./create-new-gallery.modal.html'),
	styles: [require('./create-new-gallery.modal.scss')]
})
export class CreateNewGalleryModal implements OnInit, OnDestroy {
	@Input('shootProofBrands') shootProofBrands: Array<ShootProofGalleriesBrand>;
	@Input('jobId') jobId: number;

	name: string;
	galleryName: string;
	galleryVirtualPath: string;
	galleryMainShootDate: string;
	hardcodedString: string;
	changeableString: string;
	_jobName: string;
	@Input('jobName')
	set jobName(value) {
		this.name = value;
		this._jobName = value;
	}

	get jobName() {
		return this._jobName;
	}

	eventDate;
	_mainShootDate;
	@Input('mainShootDate')
	set mainShootDate(value) {
		this.eventDate =
			value === null ? null : this.quoteUpdateService.setQuoteIssueDate(value);
		this._mainShootDate =
			value === null ? null : this.quoteUpdateService.setQuoteIssueDate(value);
	}
	get mainShootDate() {
		return this.eventDate;
	}

	datePlaceHolder = '';

	selectedBrandId = -1;
	selectedPresetId = -1;
	submitDisable = false;
	spBrandThemeId = null;

	readonly DEFAULT_GALLERY_PRESET = [{ id: -1, name: 'Choose Preset' }];
	readonly LOADING_GALLERY_PRESET = [{ id: -1, name: 'Loading...' }];

	disabledGalleryPresetSelect = false;

	galleryPresets = this.DEFAULT_GALLERY_PRESET;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private shootProofDataService: ShootProofDataProvider,
		private pickTimeDataProvider: PickTimeDataProvider,
		private messageService: MessageService,
		private settingsService: SettingsProvider,
		private quoteUpdateService: QuoteUpdateService,
		private timeService: TimeServiceProvider,
		private invoiceDataService: InvoicesDataProvider,
		private activeModal: NgbActiveModal,
		private integrationService: IntegrationsServiceProvider
	) {}

	ngOnInit(): void {
		this.initPicTimeMetadata();
		this.getTimeSettings();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	dismiss(): void {
		this.disabledGalleryPresetSelect = false;
		this.name = this.jobName;
		this.submitDisable = false;
		this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
		this.shootProofBrands = [];
		this.eventDate = this._mainShootDate;
		this.selectedBrandId = -1;
		this.selectedPresetId = -1;
		this.activeModal.close();
	}

	initPicTimeMetadata() {
		if (!this.shootProofBrands) {
			this.pickTimeDataProvider.getJobMetadata(this.jobId).subscribe(
				(data): void => {
					const { name, galleryVirtualPath, mainShootDate } = data.data;
					this.galleryName = name;
					this.galleryVirtualPath = galleryVirtualPath;
					this.galleryMainShootDate = mainShootDate;
					this.hardcodedString = `${
						this.galleryVirtualPath.replace('/-', '/-').split('/-')[0]
					}/-`;
					this.changeableString = this.galleryVirtualPath
						.replace('/-', '/-')
						.split('/-')[1]
						.toLowerCase();
				},
				error => this.messageService.handleError(error)
			);
		}
	}

	selectBrand(brandId): void {
		if (brandId != -1) {
			this.selectedBrandId = brandId;
			this.getPresets(brandId);
			this.getSelectedBrandThemeId();
		} else {
			this.selectedBrandId = -1;
			this.selectedPresetId = -1;
			this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
		}
	}

	selectPreset(presetId): void {
		this.selectedPresetId = presetId;
	}

	getSelectedBrandThemeId(): void {
		this.shootProofBrands.forEach(item => {
			if (this.selectedBrandId == item.spBrandId) {
				this.spBrandThemeId = item.spHomeBrandThemeId;
			}
		});
	}

	getPresets(BrandId: number): void {
		this.disabledGalleryPresetSelect = true;
		this.galleryPresets = this.LOADING_GALLERY_PRESET;

		this.shootProofDataService
			.getGalleryPresetsV1(BrandId)
			.finally(() => (this.disabledGalleryPresetSelect = false))
			.subscribe(
				(data: any) => {
					this.galleryPresets = this.DEFAULT_GALLERY_PRESET.concat(data.data);
				},
				error => {
					this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	private getTimeSettings(): void {
		this.settingsService.timeFormatSettings.subscribe((data): void => {
			switch (data.dateFormat) {
				case 'DD_MM_YYYY':
					this.datePlaceHolder = 'dd/mm/yyyy';
					break;
				case 'MM_DD_YYYY':
					this.datePlaceHolder = 'mm/dd/yyyy';
					break;
				default:
					this.datePlaceHolder = '';
					break;
			}
		});
	}

	private validationCreateNewGallery(): boolean {
		let validation = true;

		if (!this.name || this.name.trim() === '') {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please enter a name for your new gallery.'
			);
		} else if (this.selectedBrandId === -1) {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please select a brand for your new gallery.'
			);
		} else if (!this.eventDate || this.eventDate === null) {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please enter a shoot date for your new gallery.'
			);
		}

		return validation;
	}

	private validateProjectUrl(): boolean {
		const regex = /[a-z0-9\\_\\]+$/;
		if (!this.changeableString.match(regex)) {
			this.messageService.showToaster('e_3', 'picTime');
			return false;
		}
		return true;
	}

	onSubmit(): void {
		this.submitDisable = true;
		if (this.shootProofBrands) {
			if (!this.validationCreateNewGallery()) {
				this.submitDisable = false;
				return;
			}
			const settings = {
				jobId: this.jobId,
				spBrandId: +this.selectedBrandId,
				eventDate: this.timeService.getDateInFormat(
					this.eventDate.jsdate,
					'date'
				),
				name: this.name,
				galleryPresetId:
					this.selectedPresetId !== -1 ? +this.selectedPresetId : null,
				spBrandThemeId: this.spBrandThemeId
			};

			this.shootProofDataService
				.createShootProofNewGalleryFromSNV1(settings)
				.pipe(finalize(() => (this.submitDisable = false)))
				.subscribe(
					() => this.successCreateNewShootProofGallery(),
					error => this.messageService.handleError(error)
				);
		} else {
			if (this.galleryName.length === 0) {
				this.messageService.showToaster('e_0', 'picTime');
				this.submitDisable = false;
				return;
			} else if (this.changeableString.length === 0) {
				this.messageService.showToaster('e_1', 'picTime');
				this.submitDisable = false;
				return;
			} else if (!this.validateProjectUrl()) {
				this.submitDisable = false;
				return;
			}
			const createPicTimeGallery = {
				galleryName: this.galleryName,
				virtualPath: this.changeableString
			};
			this.pickTimeDataProvider
				.createPicTimeGallery(this.jobId, createPicTimeGallery)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize(() => (this.submitDisable = false))
				)
				.subscribe(
					(data): void => this.successCreateNewPicTimeGallery(data.data),
					error => this.messageService.handleError(error)
				);
		}
	}

	private successCreateNewShootProofGallery() {
		this.messageService.showToaster('s_1', 'shootProof');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_SP_INVOICES');
		this.dismiss();
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
	}

	private successCreateNewPicTimeGallery(data: GetRedirectPicTimeUrl) {
		this.messageService.showToaster('s_3', 'picTime');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_PT_INVOICES');
		this.dismiss();
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
		window.open(data.configureGalleryUrl, '_blank');
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=gh4osmq0bhk!./web/modern/components/integrations/connect-galleries/modals/create-new-gallery-modal/create-new-gallery.modal.ts